import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";

import { LoginUserAtom } from "../stores/LoginUserAtom";
import ConstList from "../utils/ConstList";
import { designTokens } from "../theme";
import ProjectDetail from "./ProjectDetail";

type ProjectStatus = "active" | "closed" | "deleted";
type StatusFilter = "all" | ProjectStatus;
type ReportFilter = "all" | "with" | "without";

type ProjectManagementProps = {
  customerType?: string;
  projectStatus?: "all" | "active" | "closed";
};

type CompanyOption = {
  id: number;
  name: string;
};
type OwnerOption = CompanyOption;

type ProjectRow = {
  id: number;
  companyId: number;
  companyName: string;
  projectName: string;
  projectSummary: string;
  projectStartReason: string;
  projectOwnerName: string;
  customerType: "prospective" | "existing";
  latestReportDate: string;
  nextAction: string;
  nextSchedule: string;
  progressStatus: string;
  status: ProjectStatus;
  reportCount: number;
  createdAt: string;
  createdByName: string;
  closedByName: string;
  closedReason: string;
  deletedByName: string;
};

const normalizeCompanies = (source: any): CompanyOption[] =>
  (source ?? []).map((item: any) => ({
    id: Number(item.id ?? item.company_id),
    name: String(item.name ?? item.company_name ?? ""),
  }));

const normalizeProjects = (source: any): ProjectRow[] =>
  (source ?? []).map((item: any) => ({
    id: Number(item.id ?? item.project_id),
    companyId: Number(item.company_id),
    companyName: String(item.company_name ?? ""),
    projectName: String(item.name ?? item.project_name ?? ""),
    projectSummary: String(item.project_summary ?? ""),
    projectStartReason: String(item.project_start_reason ?? ""),
    projectOwnerName: String(item.project_owner_name ?? ""),
    customerType:
      item.customer_type === "existing" || Number(item.customer_type_id) === 2
        ? "existing"
        : "prospective",
    latestReportDate: String(item.latest_report_date ?? ""),
    nextAction: String(item.next_action ?? ""),
    nextSchedule: String(item.next_schedule ?? ""),
    progressStatus: String(item.progress_status ?? ""),
    status: item.deleted_at
      ? "deleted"
      : item.status === "closed" || item.closed_at
        ? "closed"
        : "active",
    reportCount: Number(item.report_count ?? 0),
    createdAt: String(item.created_at ?? ""),
    createdByName: String(item.created_by_name ?? ""),
    closedByName: String(item.closed_by_name ?? ""),
    closedReason: String(item.closed_reason ?? ""),
    deletedByName: String(item.deleted_by_name ?? ""),
  }));

export default function ProjectManagement({
  customerType,
  projectStatus = "all",
}: ProjectManagementProps) {
  const loginUserData = useRecoilValue(LoginUserAtom);
  const location = useLocation();
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [owners, setOwners] = useState<OwnerOption[]>([]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [reportFilter, setReportFilter] = useState<ReportFilter>("all");
  const [companyFilter, setCompanyFilter] = useState<CompanyOption | null>(
    null,
  );
  const [keyword, setKeyword] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProjectRow | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState<ProjectRow | null>(null);
  const [closeReason, setCloseReason] = useState("");
  const [closeReasonOther, setCloseReasonOther] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(() => {
    const value = Number(new URLSearchParams(location.search).get("projectId"));
    return Number.isFinite(value) && value > 0 ? value : null;
  });
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(
    null,
  );
  const [projectName, setProjectName] = useState("");
  const [projectSummary, setProjectSummary] = useState("");
  const [projectStartReason, setProjectStartReason] = useState("");
  const [newCustomerType, setNewCustomerType] = useState<"prospective" | "existing">(customerType === "existing" ? "existing" : "prospective");
  const [projectOwnerId, setProjectOwnerId] = useState<number | "">("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning",
  });

  const apiRequest = useCallback(
    (payload: Record<string, unknown>) =>
      axios.post(
        ConstList.getApiUrl() + "ProjectMaster.php",
        {
          ...payload,
          customer_type: payload.customer_type ?? customerType ?? "prospective",
          access_token: loginUserData.accessToken,
          algorithm: loginUserData.algorithm,
        },
        ConstList.AXIOS_CONFIG,
      ),
    [customerType, loginUserData.accessToken, loginUserData.algorithm],
  );

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest({ flag: "Project_List" });
      const data = response.data?.data;

      setCompanies(normalizeCompanies(data?.company ?? data?.masters?.company));
      setOwners(normalizeCompanies(data?.sales_person ?? data?.masters?.sales_person));
      setProjects(normalizeProjects(data?.projects ?? data?.list));
    } catch (error) {
      console.error("案件一覧取得エラー", error);
      setSnackbar({
        open: true,
        message: "案件一覧の取得に失敗しました。",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const value = Number(new URLSearchParams(location.search).get("projectId"));
    setSelectedProjectId(Number.isFinite(value) && value > 0 ? value : null);
  }, [customerType, location.search]);

  const filteredProjects = useMemo(() => {
    const search = keyword.trim().toLocaleLowerCase("ja");

    return projects.filter((project) => {
      if (customerType && customerType !== "all" && project.customerType !== customerType) {
        return false;
      }
      const effectiveStatus =
        projectStatus !== "all"
          ? projectStatus
          : customerType === "prospective" || customerType === "existing"
            ? "active"
            : statusFilter;
      if (effectiveStatus !== "all" && project.status !== effectiveStatus) {
        return false;
      }
      if (reportFilter === "with" && project.reportCount === 0) return false;
      if (reportFilter === "without" && project.reportCount > 0) return false;
      if (companyFilter && project.companyId !== companyFilter.id) {
        return false;
      }
      return (
        !search ||
        project.projectName.toLocaleLowerCase("ja").includes(search) ||
        false
      );
    }).sort((a, b) => {
      const statusOrder: Record<ProjectStatus, number> = {
        active: 0,
        closed: 1,
        deleted: 2,
      };
      return statusOrder[a.status] - statusOrder[b.status]
        || a.companyName.localeCompare(b.companyName, "ja")
        || a.projectName.localeCompare(b.projectName, "ja");
    });
  }, [companyFilter, customerType, keyword, projectStatus, projects, reportFilter, statusFilter]);

  const createProject = async () => {
    const normalizedName = projectName.trim();
    if (!selectedCompany || !normalizedName) {
      setSnackbar({
        open: true,
        message: "会社名と案件名を入力してください。",
        severity: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await apiRequest({
        flag: "Project_Insert",
        company_id: selectedCompany.id,
        project_name: normalizedName,
        project_summary: projectSummary.trim(),
        project_start_reason: projectStartReason.trim(),
        customer_type: newCustomerType,
        project_owner_id: projectOwnerId,
      });

      if (response.data?.data === false) {
        throw new Error(response.data?.message ?? "案件を登録できません。");
      }

      setDialogOpen(false);
      setSelectedCompany(null);
      setProjectName("");
      setProjectSummary("");
      setProjectStartReason("");
      setProjectOwnerId("");
      await fetchProjects();
      setSnackbar({
        open: true,
        message: "案件を登録しました。",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "案件の登録に失敗しました。",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (project: ProjectRow, status: ProjectStatus) => {
    if (status === "closed" && !closeTarget) {
      setCloseTarget(project);
      setCloseReason("");
      setCloseReasonOther("");
      setCloseDialogOpen(true);
      return;
    }
    try {
      setLoading(true);
      const response = await apiRequest({
        flag: "Project_Update_Status",
        project_id: project.id,
        status,
        closed_reason: status === "closed"
          ? closeReason === "other" ? closeReasonOther.trim() : closeReason
          : "",
      });
      if (response.data?.data === false || response.data?.status === 0) {
        throw new Error(response.data?.message ?? "案件状態の更新に失敗しました。");
      }
      await fetchProjects();
      setCloseDialogOpen(false);
      setCloseTarget(null);
      setSnackbar({
        open: true,
        message:
          status === "closed" ? "案件を終了しました。" : "案件を再開しました。",
        severity: "success",
      });
    } catch (error) {
      console.error("案件状態更新エラー", error);
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "案件状態の更新に失敗しました。",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const restoreProject = async (project: ProjectRow) => {
    try {
      setLoading(true);
      const response = await apiRequest({
        flag: "Project_Restore",
        project_id: project.id,
      });
      if (response.data?.data === false || response.data?.status === 0) {
        throw new Error(response.data?.message ?? "案件の復元に失敗しました。");
      }
      await fetchProjects();
      setProjects((previous) =>
        previous.map((item) =>
          item.id === project.id ? { ...item, status: "active" } : item,
        ),
      );
      setSnackbar({
        open: true,
        message: "案件を復元しました。",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : "案件の復元に失敗しました。",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const requestDeleteProject = (project: ProjectRow) => {
    setDeleteTarget(project);
    setDeleteConfirmText("");
    setDeleteConfirmOpen(true);
  };

  const deleteProject = async () => {
    if (!deleteTarget || deleteConfirmText.trim() !== deleteTarget.projectName.trim()) {
      return;
    }
    const project = deleteTarget;
    setDeleteConfirmOpen(false);

    try {
      setLoading(true);
      const response = await apiRequest({
        flag: "Project_Delete",
        project_id: project.id,
      });
      if (response.data?.data === false) {
        throw new Error(response.data?.message ?? "案件を削除できません。");
      }
      await fetchProjects();
      setSnackbar({
        open: true,
        message: "案件を削除しました。",
        severity: "success",
      });
      setDeleteTarget(null);
      setDeleteConfirmText("");
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "案件の削除に失敗しました。",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo<GridColDef<ProjectRow>[]>(
    () => [
      {
        field: "companyName",
        headerName: "会社名",
        minWidth: 220,
        flex: 1,
      },
      {
        field: "projectName",
        headerName: "案件名",
        minWidth: 260,
        flex: 1.2,
      },
      {
        field: "projectOwnerName",
        headerName: "自社の営業担当者",
        minWidth: 150,
        flex: 0.8,
      },
      {
        field: "projectSummary",
        headerName: "案件概要",
        minWidth: 220,
        flex: 1,
      },
      {
        field: "customerType",
        headerName: "顧客区分",
        width: 120,
        renderCell: (params) => params.row.customerType === "prospective" ? "見込み顧客" : "既存顧客",
      },
      {
        field: "progressStatus",
        headerName: "進捗状況",
        minWidth: 140,
        flex: 0.8,
      },
      {
        field: "nextAction",
        headerName: "次回アクション",
        minWidth: 160,
        flex: 0.9,
      },
      {
        field: "nextSchedule",
        headerName: "次回予定",
        minWidth: 120,
        flex: 0.7,
        renderCell: (params) => params.row.nextSchedule ? String(params.row.nextSchedule).slice(0, 10) : "未設定",
      },
      {
        field: "latestReportDate",
        headerName: "最終日報登録日",
        minWidth: 150,
        flex: 0.7,
        valueFormatter: (value) => String(value ?? "未登録").slice(0, 10),
      },
      {
        field: "status",
        headerName: "状態",
        width: 110,
        renderCell: (params) => (
          <Chip
            size="small"
            label={
              params.row.status === "active"
                ? "進行中"
                : params.row.status === "closed"
                  ? "終了"
                  : "削除済み"
            }
            color={
              params.row.status === "active"
                ? "success"
                : params.row.status === "deleted"
                  ? "error"
                  : "default"
            }
            variant="outlined"
          />
        ),
      },
      {
        field: "createdByName",
        headerName: "登録者",
        minWidth: 140,
        flex: 0.7,
      },
      {
        field: "closedByName",
        headerName: "終了者",
        minWidth: 140,
        flex: 0.7,
      },
      {
        field: "deletedByName",
        headerName: "削除者",
        minWidth: 140,
        flex: 0.7,
      },
      {
        field: "reportCount",
        headerName: "日報件数",
        width: 110,
      },
      {
        field: "actions",
        headerName: "操作",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip
              title={
                params.row.status === "deleted"
                  ? "案件を復元"
                  : params.row.status === "active"
                    ? "案件を終了"
                    : "案件を再開"
              }
            >
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  if (params.row.status === "deleted") {
                    void restoreProject(params.row);
                  } else {
                    void changeStatus(
                      params.row,
                      params.row.status === "active" ? "closed" : "active",
                    );
                  }
                }}
              >
                {params.row.status === "active" ? (
                  <StopCircleOutlinedIcon />
                ) : (
                  <ReplayRoundedIcon />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="案件を削除">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  sx={{
                    display:
                      params.row.status === "deleted" ? "none" : "inline-flex",
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    requestDeleteProject(params.row);
                  }}
                >
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [changeStatus, requestDeleteProject, restoreProject],
  );

  const visibleColumns = columns.filter((column) => {
    const baseFields = [
      "companyName",
      "projectName",
      "projectSummary",
      "customerType",
      "progressStatus",
      "nextAction",
      "nextSchedule",
      "latestReportDate",
      "createdByName",
      "reportCount",
      "status",
      "actions",
    ];
    if (!baseFields.includes(String(column.field))) return false;
    return !(
      column.field === "status" &&
      (customerType === "prospective" || customerType === "existing")
    );
  });

  const orderedVisibleColumns =
    projectStatus === "all" &&
    customerType !== "prospective" &&
    customerType !== "existing"
      ? [
          "status",
          "companyName",
          "projectName",
          "projectSummary",
          "nextSchedule",
          "latestReportDate",
          "customerType",
          "actions",
        ]
          .map((field) => visibleColumns.find((column) => column.field === field))
          .filter((column): column is (typeof visibleColumns)[number] => Boolean(column))
      : visibleColumns;

  if (selectedProjectId !== null) {
    return <ProjectDetail projectId={selectedProjectId} onBack={() => { setSelectedProjectId(null); void fetchProjects(); }} />;
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2.5 },
          borderRadius: 3,
          border: `1px solid ${designTokens.border.default}`,
          backgroundColor: designTokens.surface.paper,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Typography component="h2" fontSize={18} fontWeight={900}>
                案件管理
              </Typography>
              <Typography color="text.secondary" fontSize={13} mt={0.5}>
                終了・削除した案件は日報の選択肢から外れます。削除後も過去の日報と操作履歴は保持されます。
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{
                backgroundColor: designTokens.brand.primary,
                color: designTokens.text.primary,
                fontWeight: 800,
              }}
            >
              案件を追加
            </Button>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField
              size="small"
              label="案件名で検索"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              sx={{ width: { xs: "100%", md: 280 }, flex: { md: "0 0 280px" }, ml: { md: 2 }, order: { xs: 2, md: 2 } }}
              placeholder="案件名で検索"
            />
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.name}
              value={companyFilter}
              onChange={(_, value) => setCompanyFilter(value)}
              sx={{ width: { md: 240 }, flex: { md: "0 0 240px" }, order: { xs: 1, md: 1 } }}
              renderInput={(params) => (
                <TextField {...params} size="small" label="会社で絞り込み" />
              )}
            />
            <TextField
              select
              size="small"
              label="状態"
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value as StatusFilter)
              }
              sx={{ minWidth: 140, display: "none" }}
            >
              <MenuItem value="active">進行中</MenuItem>
              <MenuItem value="closed">終了</MenuItem>
              <MenuItem value="deleted">削除済み</MenuItem>
              <MenuItem value="all">すべて</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="日報"
              value={reportFilter}
              onChange={(event) => setReportFilter(event.target.value as ReportFilter)}
              sx={{ minWidth: 140, display: "none" }}
            >
              <MenuItem value="all">日報すべて</MenuItem>
              <MenuItem value="with">日報あり</MenuItem>
              <MenuItem value="without">日報なし</MenuItem>
            </TextField>
          </Stack>

          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", minHeight: 28 }}>
            <Typography variant="body2" fontWeight={800} color="text.secondary">総案件数：{filteredProjects.length}件</Typography>
          </Box>

          <Box sx={{ display: "none", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
            {[["表示案件", filteredProjects.length, designTokens.brand.primary], ["進行中", filteredProjects.filter((project) => project.status === "active").length, "#2563EB"], ["日報あり", filteredProjects.filter((project) => project.reportCount > 0).length, "#7C3AED"], ["次回予定超過", filteredProjects.filter((project) => project.nextSchedule && project.nextSchedule.slice(0, 10) < new Date().toISOString().slice(0, 10)).length, "#DC2626"]].map(([label, value, color]) => <Paper key={String(label)} elevation={0} sx={{ p: 1.5, border: `1px solid ${designTokens.border.default}`, borderLeft: `4px solid ${color}`, backgroundColor: designTokens.surface.paper }}><Typography variant="caption" color="text.secondary" fontWeight={800}>{label}</Typography><Typography variant="h6" fontWeight={900}>{value}</Typography></Paper>)}
          </Box>

          <Box height={{ xs: 380, sm: 440, md: 480 }}>
            <DataGridPro
              rows={filteredProjects}
              columns={orderedVisibleColumns}
              loading={loading}
              disableRowSelectionOnClick
              onRowClick={(params) => setSelectedProjectId(params.row.id)}
              hideFooter
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" }, "& .MuiDataGrid-row:hover": { backgroundColor: designTokens.surface.subtle }, "& .MuiDataGrid-columnHeaders": { backgroundColor: designTokens.surface.strong, fontWeight: 900 }, "& .MuiDataGrid-cell[data-field=\"companyName\"], & .MuiDataGrid-cell[data-field=\"projectName\"]": { fontWeight: 700 } }}
              localeText={{
                noRowsLabel: "対象の案件はありません。",
                noResultsOverlayLabel: "条件に一致する案件はありません。",
              }}
            />
          </Box>
        </Stack>
      </Paper>

      <Dialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>案件を終了</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>終了理由を選択してください。</Typography>
          <TextField
            select
            fullWidth
            label="終了理由"
            value={closeReason}
            onChange={(event) => setCloseReason(event.target.value)}
            disabled={loading}
          >
            <MenuItem value="受注">受注</MenuItem>
            <MenuItem value="失注">失注</MenuItem>
            <MenuItem value="予算都合">予算都合</MenuItem>
            <MenuItem value="顧客都合">顧客都合</MenuItem>
            <MenuItem value="他社へ移行">他社へ移行</MenuItem>
            <MenuItem value="保留・時期未定">保留・時期未定</MenuItem>
            <MenuItem value="other">その他</MenuItem>
          </TextField>
          {closeReason === "other" && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="終了理由（自由記述）"
              value={closeReasonOther}
              onChange={(event) => setCloseReasonOther(event.target.value)}
              sx={{ mt: 2 }}
              disabled={loading}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)} disabled={loading}>キャンセル</Button>
          <Button
            variant="contained"
            disabled={!closeTarget || !closeReason || (closeReason === "other" && !closeReasonOther.trim()) || loading}
            onClick={() => closeTarget && void changeStatus(closeTarget, "closed")}
          >
            終了する
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>案件を削除しますか？</DialogTitle>
        <DialogContent>
          <Typography>
            削除後は案件一覧に表示されなくなります。日報と操作履歴はDBに保持されます。
          </Typography>
          <Typography mt={2} fontWeight={800}>
            確認のため、案件名「{deleteTarget?.projectName ?? ""}」を入力してください。
          </Typography>
          <TextField
            fullWidth
            autoFocus
            size="small"
            sx={{ mt: 2 }}
            label="案件名を入力"
            value={deleteConfirmText}
            onChange={(event) => setDeleteConfirmText(event.target.value)}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={loading}>
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={
              loading ||
              !deleteTarget ||
              deleteConfirmText.trim() !== deleteTarget.projectName.trim()
            }
            onClick={() => void deleteProject()}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>案件を追加</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.name}
              value={selectedCompany}
              onChange={(_, value) => setSelectedCompany(value)}
              renderInput={(params) => (
                <TextField {...params} required label="会社名" />
              )}
            />
            <TextField
              required
              label="案件名"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              helperText="同じ会社に同名の案件は登録できません。"
            />
            <TextField
              select
              label="顧客区分"
              value={newCustomerType}
              onChange={(event) => setNewCustomerType(event.target.value as "prospective" | "existing")}
            >
              <MenuItem value="prospective">見込み顧客</MenuItem>
              <MenuItem value="existing">既存顧客</MenuItem>
            </TextField>
            <TextField
              select
              label="自社の営業担当者"
              value={projectOwnerId}
              onChange={(event) => setProjectOwnerId(event.target.value === "" ? "" : Number(event.target.value))}
            >
              <MenuItem value="">未選択</MenuItem>
              {owners.map((owner) => <MenuItem key={owner.id} value={owner.id}>{owner.name}</MenuItem>)}
            </TextField>
            <TextField
              multiline
              minRows={2}
              label="案件概要"
              value={projectSummary}
              onChange={(event) => setProjectSummary(event.target.value)}
            />
            <TextField
              multiline
              minRows={2}
              label="案件開始理由"
              value={projectStartReason}
              onChange={(event) => setProjectStartReason(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={createProject}>
            登録
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((current) => ({ ...current, open: false }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
